import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authApi, usersApi, setAccessToken, setRefreshToken, getAccessToken, getRefreshToken, clearTokens } from '@/lib/api';
import type { User } from '@/types';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const defaultAuthContext: AuthContextType = {
  user: null,
  isLoading: true,
  isAuthenticated: false,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
  refreshUser: async () => {},
};

// Track ongoing auth initialization to prevent duplicate requests in Strict Mode
let authInitPromise: Promise<void> | null = null;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const fetchUser = useCallback(async (): Promise<User | null> => {
    try {
      const response = await usersApi.getMe();
      return response.data;
    } catch {
      clearTokens();
      return null;
    }
  }, []);

  const initAuth = useCallback(async () => {
    // Prevent duplicate auth initialization (React Strict Mode calls useEffect twice)
    if (authInitPromise) {
      await authInitPromise;
      return;
    }

    const doInit = async () => {
      const storedRefreshToken = getRefreshToken();
      if (!storedRefreshToken) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await authApi.refresh(storedRefreshToken);
        setAccessToken(response.data.accessToken);
        setRefreshToken(response.data.refreshToken);
        const userData = await fetchUser();
        // Use flushSync to ensure both state updates happen synchronously
        // This prevents race conditions where isLoading=false but user is not yet set
        flushSync(() => {
          setUser(userData);
          setIsLoading(false);
        });
      } catch {
        flushSync(() => {
          setUser(null);
          clearTokens();
          setIsLoading(false);
        });
      }
    };

    authInitPromise = doInit();
    try {
      await authInitPromise;
    } finally {
      authInitPromise = null;
    }
  }, [fetchUser]);

  useEffect(() => {
    void initAuth();
  }, [initAuth]);

  const login = async (email: string, password: string) => {
    const response = await authApi.login({ email, password });
    const { tokens, user: userData } = response.data;
    setAccessToken(tokens.accessToken);
    setRefreshToken(tokens.refreshToken);
    setUser(userData);
    navigate(userData.role === 'client' ? '/dashboard' : '/start');
  };

  const register = async (email: string, password: string, displayName: string) => {
    const response = await authApi.register({ email, password, displayName });
    const { tokens, user: userData } = response.data;
    setAccessToken(tokens.accessToken);
    setRefreshToken(tokens.refreshToken);
    setUser(userData);
    navigate('/dashboard');
  };

  const logout = async () => {
    try {
      const currentRefreshToken = getRefreshToken();
      await authApi.logout(currentRefreshToken || undefined);
    } finally {
      setUser(null);
      clearTokens();
      queryClient.clear();
      navigate('/login');
    }
  };

  const refreshUser = async () => {
    const userData = await fetchUser();
    setUser(userData);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user && !!getAccessToken(),
        login,
        register,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- hook paired with AuthProvider in this module
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  // When outside provider (e.g. React Strict Mode unmount), return safe defaults to avoid crash
  return context ?? defaultAuthContext;
}
